import {DOCUMENT} from '@angular/common';
import {ApplicationRef, DestroyRef, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {SwUpdate} from '@angular/service-worker';
import {concat, interval} from 'rxjs';
import {filter, first} from 'rxjs/operators';

const updateCheckIntervalMs = 60 * 60 * 1000;

/** Coordinates service-worker update checks and activation for the application shell. */
@Injectable({providedIn: 'root'})
export class AppUpdateService {
  private readonly appRef = inject(ApplicationRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly updates = inject(SwUpdate);
  private initialized = false;

  readonly isUpdateAvailable = signal(false);
  readonly isUpdating = signal(false);

  /** Starts update polling once the initial application render has stabilized. */
  init(): void {
    if (this.initialized || !this.updates.isEnabled) {
      return;
    }

    this.initialized = true;

    const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable));
    const everyHour$ = interval(updateCheckIntervalMs);

    concat(appIsStable$, everyHour$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.checkForUpdate());

    this.updates.versionUpdates
      .pipe(
        filter((event) => event.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.isUpdateAvailable.set(true));
  }

  /** Activates the downloaded version and reloads the page into that version. */
  async activateUpdate(): Promise<void> {
    if (this.isUpdating()) {
      return;
    }

    this.isUpdating.set(true);

    try {
      await this.updates.activateUpdate();
      this.document.defaultView?.location.reload();
    } catch (error: unknown) {
      this.isUpdating.set(false);
      console.error('The application update could not be activated.', error);
    }
  }

  /**
   * Checks the service-worker manifest for a newer application version.
   *
   * This method runs after the application becomes stable and then hourly. A detected update is
   * downloaded in the background; the version update subscription displays the confirmation modal
   * once the complete version is ready. Check failures are logged and do not interrupt the app.
   *
   * @return A promise that resolves after the update check completes or fails gracefully.
   */
  private async checkForUpdate(): Promise<void> {
    try {
      await this.updates.checkForUpdate();
    } catch (error: unknown) {
      console.error('The application update could not be checked.', error);
    }
  }
}
